import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    useTheme,
    alpha,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Star as StarIcon,
    LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { billingApi } from '../services/api';
import { useLocation } from 'react-router-dom';
import useRazorpay from '../hooks/useRazorpay';

const Pricing = () => {
    const theme = useTheme();
    const location = useLocation();
    const [config, setConfig] = useState(null);
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [currency, setCurrency] = useState('usd');

    const { openCheckout: openRazorpay, loading: razorpayLoading } = useRazorpay({
        onError: (msg) => setError(msg),
    });

    // Check URL parameters for success or canceled from Stripe redirect
    const queryParams = new URLSearchParams(location.search);
    const isSuccess = queryParams.get('success') === 'true';
    const isCanceled = queryParams.get('canceled') === 'true';

    useEffect(() => {
        Promise.all([
            billingApi.getConfig().then(setConfig).catch(() => setError('Could not load pricing plans. Please try again later.')),
            billingApi.getActiveDiscounts().then(setDiscounts).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, []);

    // Find the best active coupon for a given planId (plan-specific > global)
    const getCouponForPlan = (planId) => {
        const specific = discounts.find(d => d.applies_to_plans && d.applies_to_plans.includes(planId));
        if (specific) return specific;
        const global = discounts.find(d => !d.applies_to_plans || d.applies_to_plans.length === 0);
        return global || null;
    };

    // M2: show the discount amount in the selected currency symbol
    const formatDiscount = (coupon) => {
        if (!coupon) return '';
        if (coupon.discount_type === 'percent') return `${coupon.amount}% off`;
        const symbol = currency === 'inr' ? '₹' : '$';
        return `${symbol}${(coupon.amount / 100).toFixed(0)} off`;
    };

    const handleSubscribe = async (planId) => {
        setError('');

        // INR users → Razorpay (supports UPI, netbanking, wallets)
        // USD users → Stripe (card payments)
        if (currency === 'inr') {
            setCheckoutLoading(planId);
            try {
                await openRazorpay(planId);
            } finally {
                setCheckoutLoading(null);
            }
            return;
        }

        // Stripe flow for USD
        setCheckoutLoading(planId);
        try {
            const coupon = getCouponForPlan(planId);
            const { url } = await billingApi.createCheckoutSession(planId, currency, coupon?.id || null);
            if (url) window.location.href = url;
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError('Failed to initiate checkout. Please try again.');
        } finally {
            setCheckoutLoading(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const plans = config?.plans || {};

    return (
        <Box sx={{ maxWidth: 860, mx: 'auto', py: 4, px: { xs: 2, md: 3 } }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, px: 2, py: 1, borderRadius: 8, mb: 2 }}>
                    <StarIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Upgrade Your Empire</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>Simple, transparent pricing</Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                    Unlock the full power of AI for your social media strategy.
                </Typography>

                <ToggleButtonGroup
                    value={currency}
                    exclusive
                    onChange={(e, newCurrency) => {
                        if (newCurrency !== null) {
                            setCurrency(newCurrency);
                        }
                    }}
                    aria-label="currency"
                    size="small"
                >
                    <ToggleButton value="usd" aria-label="usd" sx={{ px: 3, fontWeight: 700 }}>
                        USD ($)
                    </ToggleButton>
                    <ToggleButton value="inr" aria-label="inr" sx={{ px: 3, fontWeight: 700 }}>
                        INR (₹)
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {isSuccess && (
                <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                    Subscription successful! Your account has been upgraded. Please wait a few moments for the new claims to reflect.
                </Alert>
            )}

            {isCanceled && (
                <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
                    Checkout was canceled. No charges were made.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {Object.keys(plans).length === 0 && (
                <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                    No plans are available right now. Please check back shortly.
                </Alert>
            )}

            {/* CSS Grid — guarantees equal-width equal-height cards per row */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3,
            }}>
                {Object.entries(plans)
                    .sort(([, a], [, b]) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
                    .map(([planId, planDetails]) => {
                    const coupon = getCouponForPlan(planId);
                    const discountLabel = formatDiscount(coupon);
                    return (
                    <Card key={planId} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)' },
                    }}>
                        {/* Offer badge */}
                        {coupon && (
                            <Tooltip title={coupon.name} placement="top">
                                <Chip
                                    icon={<OfferIcon sx={{ fontSize: '14px !important' }} />}
                                    label={discountLabel}
                                    size="small"
                                    sx={{
                                        position: 'absolute', top: 12, left: 12, fontWeight: 700,
                                        bgcolor: 'success.main', color: 'white',
                                        '& .MuiChip-icon': { color: 'white' },
                                    }}
                                />
                            </Tooltip>
                        )}
                        {planDetails.badge && (
                            <Chip
                                label={planDetails.badge}
                                color="primary"
                                size="small"
                                sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 700 }}
                            />
                        )}
                        <CardContent sx={{ flexGrow: 1, p: 3, pt: coupon ? 5 : 3, display: 'flex', flexDirection: 'column' }}>
                            {/* Name — fixed height so price aligns across same-row cards */}
                            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, minHeight: 64, display: 'flex', alignItems: 'flex-end' }}>
                                {planDetails.name}
                            </Typography>

                            {/* Price */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
                                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                    {currency === 'inr' ? '₹' : '$'}
                                    {currency === 'inr'
                                        ? planDetails.price?.inr?.replace('₹', '')
                                        : planDetails.price?.usd?.replace('$', '')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">/ month</Typography>
                                {coupon && (
                                    <Typography variant="caption" sx={{
                                        color: 'success.main', fontWeight: 700,
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        px: 1, py: 0.25, borderRadius: 1,
                                    }}>
                                        {coupon.name}
                                    </Typography>
                                )}
                            </Box>

                            {/* Subscribe button */}
                            <Button
                                fullWidth
                                variant={planDetails.name === 'Creator Pro' ? 'contained' : 'outlined'}
                                size="large"
                                onClick={() => handleSubscribe(planId)}
                                disabled={checkoutLoading === planId || razorpayLoading}
                                sx={{ mb: 3, py: 1.5 }}
                            >
                                {checkoutLoading === planId
                                    ? <CircularProgress size={24} />
                                    : coupon ? `Subscribe · ${discountLabel}` : (currency === 'inr' ? 'Pay with UPI / Card' : 'Subscribe Now')}
                            </Button>

                            {/* Features */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textTransform: 'uppercase', color: 'text.secondary' }}>
                                What's included:
                            </Typography>
                            <List dense disablePadding sx={{ flexGrow: 1 }}>
                                {(currency === 'inr' ? (planDetails.features_ind || []) : (planDetails.features_global || [])).map((feature, idx) => (
                                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <CheckCircleIcon color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={feature} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Pricing;
